import { Fragment, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import ZoomViewer from '@components/ZoomViewer'
import useControls from './hooks/useControls'
import { Container, Row, Col } from '@mch-group/uikit-components'
import { generateImagesId } from './contexts/images'
import useImagesInformation from './hooks/useImagesInformation'
import { useModuleContext } from './contexts/module'

/**
 * @returns {JSX.Element}
 * @constructor
 */
const Image = ({ url, height, position, isVideo = false, style, totalCount, imgCount }) => {
  const { activeImageId } = useImagesInformation()
  const minHeight = height / 3 - 300
  const isActive = position === activeImageId

  /** Can be a video :p **/

  const content = isVideo ? (
    <VideoWrapper>
      {/* <Embed
        active
        autoplay={false}
        source={videoType}
        id={videoId}
        iframe={{
          allowFullScreen: true,
          id: 'videoIframe'
        }}
      /> */}
      <div className='text medium color-black left-0 p-4 position-absolute bg-white text-center'>
        <b>{imgCount}</b> / {totalCount}
      </div>
    </VideoWrapper>
  ) : null

  return (
    <ImageContainer
      className='__ad-gallery-image-or-video mb-9'
      //@ts-ignore
      isVideo={isVideo}
      minHeight={minHeight}
      key={url}
      id={generateImagesId(position)}
      backgroundColor='var(--bs-mch-white)'
      active={isActive}
      position={position}
      isPrevious={position < activeImageId}
      style={style}
    >
      {content}
    </ImageContainer>
  )
}

const VideoWrapper = styled.div`
  @media (max-width: ${global.uikitVars.breakpointMdMax}) {
    position: relative;
  }
`


const ImageContainer = styled.div<any>`
  scroll-margin: 140px;
  background: ${
    //@ts-ignore
    ({ backgroundColor }) => backgroundColor
  };
  position: relative;

  & .ui.embed {
    background: ${({
      backgroundColor
    }) => backgroundColor} !important;

    @media (min-width: 992px) {
      padding-bottom: 40%;
    }
  }

  @media (max-width: ${global.uikitVars.breakpointMdMax}) {
    scroll-snap-align: center;
  }
`

Image.propTypes = {
  url: PropTypes.string,
  position: PropTypes.number,
  height: PropTypes.number,
  isVideo: PropTypes.bool,
  videoType: PropTypes.string,
  videoId: PropTypes.string,
  width: PropTypes.number,
  style: PropTypes.object,
  totalCount: PropTypes.number,
  imgCount: PropTypes.number,
  alt: PropTypes.string
}

/**
 * Image or videos ;)
 * As the video is always the last, the position will be the number of images, as arrays start at 0 :()
 * @returns {JSX.Element}
 * @constructor
 */
function Images() {
  const { state: { artworkInformation, videos, labels } } = useModuleContext()
  const { images, id: artworkId, videoPosition, name: artworkName } = artworkInformation || {}
  const { id, isVideoSet, videoUrl, videoType } = videos || {}

  const {
    handleDotClick
  } = useControls()
  const ref = useRef()

  // There is a "legacy" of Artworks that don't have videoPosition but still have video. For those, v.p. should be 1
  let videoPositionExisting = videoPosition
  if (isVideoSet && videoPosition === null) {
    videoPositionExisting = 1
  }

  /** When switching artworks, reset to position 1 **/
  useEffect(() => {
    /** I'm modifying this JosepV line to adapt to the video-in-first-position feature.
    * handleDotClick function moves the scroll to a certain Artwork.
    * In this case, it is in a Hook to be executed on every artwork load, so the page scrolls to the top.
    * Behavior has only been modified for the video-in-first-position feature.
    * This func was assuming "0" would be the first item always. Items have IDs like artwork--0, artwork--1.
    * Since the video, I am using "CSS Flex" Order. So video is the last in the DOM, but first in the list, and it does
    * not correspond to artwork--0. So, to know what is the value for the video I just get the lentgh of images.
    */
    handleDotClick(0, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworkId])

  // Array containing the components to render. 1 pos of the array is images list, second is the video.
  const componentsArray = [
    () => (
      <ZoomViewer
      // @ts-ignore
        images={images}
        // @ts-ignore
        totalCount={id ? images.length + 1 : images.length}
        // @ts-ignore
        videoPosition={videoPositionExisting}
        title={artworkName}
        fillBg
        altText={`${artworkInformation?.artworkInfo?.displayName} ${labels?.AltTags?.altTagBy} ${
          artworkInformation?.artworkInfo?.artistName
        } ${artworkInformation?.artworkInfo?.year ? `(${artworkInformation?.artworkInfo?.year})` : ''}`}
      />
    ),
    isVideoSet ? () => (
      <Image
        alt=''
        isVideo
        height={650}
        // @ts-ignore
        videoId={id}
        videoType={videoType}
        url={videoUrl}
        // @ts-ignore
        position={videoPositionExisting === 0 ? 0 : images.length}
        // @ts-ignore
        totalCount={images.length + 1}
        // @ts-ignore
        imgCount={videoPositionExisting === 0 ? 1 : images.length + 1}
      />
    ) : null
  ]

  if (!images || images.length < 1) return null

  return (
    <Fragment>
      {/* @ts-ignore */}
      <ImagesContainer id='__ad-images-container' ref={ref} className='mt-0'>
        <Container className='px-5 px-lg-8 px-md-7'>
          <Row>
            <Col className='text-center mt-12'>
              {/* I would have prefered to map the Array, but the scroll hooks above didn't respond correctly. */}
              {videoPositionExisting === 0 &&
            (
              <>
                {/* First the video, then the images */ /* @ts-ignore */}
                {componentsArray[1] && componentsArray[1]()}
                {/* @ts-ignore */}
                {componentsArray[0] && componentsArray[0]()}
              </>
            )}
              {videoPositionExisting === 1 &&
            (
              <>
                {/* First the list of images, then the video */ /*@ts-ignore */}
                {componentsArray[0] && componentsArray[0]()}
                {/* @ts-ignore */}
                {componentsArray[1] && componentsArray[1]()}
              </>
            )}
              {typeof videoPositionExisting === 'undefined' &&
            (
              <>
                {/* Just the images */ /*@ts-ignore */}
                {componentsArray[0] && componentsArray[0]()}
              </>
            )}
            </Col>
          </Row>
        </Container>
      </ImagesContainer>
    </Fragment>
  )
}

const ImagesContainer = styled.div``

export default Images
